import type { PrybarNavigationEvent } from "@prybar/core";

// app.navigate returns "/route#prybar=<target>". After the route renders, find
// the element marked data-prybar-target=<target>, scroll to it, and pulse it.
export function pulsePrybarTarget(target: string, attempt = 0): void {
  // A retry can outlive the page it was scheduled on, such as a test environment torn down mid-wait.
  if (typeof document === "undefined") return;
  const el = document.querySelector<HTMLElement>(`[data-prybar-target="${target}"]`);
  if (!el) {
    // page still rendering: retry briefly
    if (attempt < 12) window.setTimeout(() => pulsePrybarTarget(target, attempt + 1), 150);
    return;
  }
  // The command bar locks page scroll (overflow:hidden on body/html) while open,
  // so scrollIntoView is a no-op until it closes. Wait for the unlock, then scroll,
  // or the page stays at the top and the pulse fires off-screen. The window is
  // generous so it survives a spoken narration that keeps the bar open first.
  const locked =
    document.body.style.overflow === "hidden" ||
    document.documentElement.style.overflow === "hidden";
  if (locked) {
    if (attempt < 80) window.setTimeout(() => pulsePrybarTarget(target, attempt + 1), 150);
    return;
  }
  if (typeof el.scrollIntoView === "function") {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  el.classList.add("prybar-pulse");
  window.setTimeout(() => el.classList.remove("prybar-pulse"), 2200);
}

export function targetFromRoute(route: string): string | null {
  const hash = route.split("#")[1];
  const match = hash?.match(/(?:^|&)prybar=([^&]+)/);
  return match ? decodeURIComponent(match[1] ?? "") : null;
}

export function handlePrybarNavigate(event: PrybarNavigationEvent): void {
  const target = targetFromRoute(event.route);
  if (target) window.setTimeout(() => pulsePrybarTarget(target), 300);
}
